import { FACET_POOLS, FACET_LABELS } from "./facets";

/**
 * Game-Jam Idea Generator content. Reuses the shared facet pools and adds a
 * jam-specific "Twist" slot of real jam-style themes/constraints. Supports two
 * modes: an OPEN prompt (twist only) and a guided FULL concept. Rolls honor a
 * craftable selection (which axes/values may roll).
 */

/** Curated jam themes / design constraints (the heart of the Jam feature). */
export const TWISTS = [
  "Only One",
  "Out of Control",
  "Joined Together",
  "Roll of the Dice",
  "Roles Reversed",
  "Built to Scale",
  "There and Back",
  "Loop",
  "Dual-Purpose Design",
  "Genre Without Its Core Mechanic",
  "Sacrifice for Power",
  "The World Is Shrinking",
  "You Are the Enemy",
  "Death Is Progress",
  "Less Is More",
  "Two of Everything",
  "Stronger Together",
  "Keep It Alive",
  "Connected Worlds",
  "Limited Space",
  "Every Ten Seconds",
  "Rewind",
  "Combine and Merge",
  "No Combat",
  "One Button",
  "Hidden Information",
  "Shared Health",
  "It Grows",
  "Mistakes Are Permanent",
  "The Narrator Lies",
  "Gravity Is a Tool",
  "Time Moves When You Move",
  "Everything Is Connected",
  "You Can't Stop Moving",
  "Build the Thing That Destroys You",
  "Trade Power for Knowledge",
  "One Room, Many Times",
  "The Tool Is the Enemy",
  "Grow Then Shrink",
  "Cooperate to Compete",
  "The Map Is the Mechanic",
  "Health Is Currency",
  "Light and Shadow",
  "Everything Decays",
  "Possess Anything",
  "Your Past Selves Help You",
  "Silence Is Safety",
  "Break to Progress",
  "The Crowd Decides",
  "Reverse the Genre",
  "Small World, Big Stakes",
  "One Life, Many Bodies",
  "The Rules Change Mid-Game",
];

/** Optional one-line hints to interpret the more abstract themes. */
export const TWIST_HINTS: Record<string, string> = {
  "Only One": "Build everything around a single instance of something (one life, one room, one bullet).",
  "Out of Control": "The player only has partial control; something acts on its own.",
  "Joined Together": "Two things that are normally separate are bound into one.",
  "Roll of the Dice": "Embrace randomness as a core, fair mechanic.",
  "Roles Reversed": "Flip the expected role — the hunted becomes the hunter.",
  "Built to Scale": "Scale (size, zoom, or quantity) is the central mechanic.",
  "There and Back": "Every journey out must return; the trip back matters.",
  "Loop": "The experience repeats — learn and exploit the cycle.",
  "Dual-Purpose Design": "One input or object serves two opposing purposes (à la Downwell).",
  "Genre Without Its Core Mechanic": "Take a genre and remove its defining verb.",
  "Sacrifice for Power": "Getting stronger always costs something dear.",
  "Less Is More": "Removing options or resources is the path to mastery.",
  "Connected Worlds": "Two spaces affect each other in real time.",
  "Every Ten Seconds": "A recurring beat on a strict timer drives play.",
  "It Grows": "Something expands over the session and reshapes play.",
  "The Narrator Lies": "The information given to the player can't be trusted.",
  "Gravity Is a Tool": "The player bends gravity to solve problems rather than fight it.",
  "Time Moves When You Move": "The world is frozen until the player acts (à la SUPERHOT).",
  "Everything Is Connected": "Touching one thing visibly affects everything else.",
  "You Can't Stop Moving": "Standing still isn't an option; momentum is mandatory.",
  "Build the Thing That Destroys You": "The player creates their own escalating threat.",
  "Trade Power for Knowledge": "Learning a secret costs you strength, and vice versa.",
  "One Room, Many Times": "A single space, revisited, changes meaning each pass.",
  "The Tool Is the Enemy": "Your main tool is also the thing trying to stop you.",
  "Cooperate to Compete": "Players must help each other to have any chance of winning.",
  "The Map Is the Mechanic": "Navigating or reshaping the map IS the core verb.",
  "Health Is Currency": "Spend your life force to act; survival and power compete.",
  "Light and Shadow": "Visibility — yours and enemies' — is the central resource.",
  "Everything Decays": "Nothing is permanent; entropy is the pressure.",
  "Possess Anything": "Control passes from body to body across the world.",
  "Your Past Selves Help You": "Recordings of prior runs become co-op partners.",
  "Silence Is Safety": "Sound attracts danger; quiet is a mechanic.",
  "Break to Progress": "Destroying or sacrificing things is the only way forward.",
  "The Crowd Decides": "An audience (real or simulated) shapes the rules live.",
  "Reverse the Genre": "Invert a genre's goal — a tower offense, a pacifist shooter.",
  "One Life, Many Bodies": "A single consciousness, a limited supply of disposable bodies.",
  "The Rules Change Mid-Game": "The ruleset shifts partway through and you must adapt.",
};

/** Slot keys are dynamic (twist + built-in/custom concept axes), so plain strings. */
export type JamSlotKey = string;

/** The jam-specific slot plus the built-in concept axes (custom axes are added at runtime). */
export const JAM_SLOTS: JamSlotKey[] = [
  "twist", "theme", "genre", "subgenre", "mechanic", "emotion", "artStyle",
  "camera", "gameMode", "audience", "platform",
];

export const JAM_SLOT_LABELS: Record<string, string> = {
  ...FACET_LABELS,
  twist: "Twist / Theme",
};

export const JAM_POOLS: Record<string, string[]> = {
  ...FACET_POOLS,
  twist: TWISTS,
};

export interface JamConcept {
  title: string;
  [key: string]: string;
}
export type JamSelection = Record<string, string[]>;

const NAME_PREFIX = [
  "Neon", "Echoes of", "Project", "Hyper", "The Last", "Mega", "Pocket", "Eternal",
  "Broken", "Cosmic", "Velvet", "Iron", "Whisper", "Crimson", "Hollow", "Wandering",
  "Final", "Lone", "Endless", "Forgotten", "Astral", "Rusted", "Lucid", "Phantom",
];
const NAME_SUFFIX = [
  "Protocol", "Saga", "Drift", "Requiem", "Engine", "Bloom", "Circuit", "Horizon",
  "Descent", "Pulse", "Reverie", "Foundry", "Ascent", "Verge", "Threshold", "Echo",
  "Gambit", "Odyssey", "Loop", "Spire", "Covenant", "Mirage", "Cascade", "Vanguard",
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export const rollName = () => `${pick(NAME_PREFIX)} ${pick(NAME_SUFFIX)}`;
export const rollFrom = (values: string[]) => pick(values);
export const twistHint = (title: string) => TWIST_HINTS[title] ?? "";

/** Roll a concept honoring a selection (each enabled axis with values, in order). */
export function rollConcept(selection?: JamSelection): JamConcept {
  const c: JamConcept = { title: rollName() };
  const entries = selection ? Object.entries(selection) : JAM_SLOTS.map((k) => [k, JAM_POOLS[k]] as [string, string[]]);
  for (const [key, values] of entries) {
    if (values && values.length) c[key] = pick(values);
  }
  return c;
}

/** A templated pitch that adapts to whichever slots are present. */
export function buildPitch(c: JamConcept): string {
  const bits: string[] = [];
  const conceptKeys: JamSlotKey[] = ["theme", "genre", "subgenre", "platform", "camera", "gameMode", "mechanic", "emotion", "artStyle", "audience"];
  const hasConcept = conceptKeys.some((k) => c[k]);

  if (hasConcept) {
    let s = `${c.title} is`;
    if (c.theme) s += ` a ${c.theme.toLowerCase()}`;
    s += c.genre ? ` ${c.genre.toLowerCase()}` : " game";
    if (c.subgenre) s += ` (${c.subgenre.toLowerCase()})`;
    if (c.artStyle) s += ` with a ${c.artStyle.toLowerCase()} look`;
    if (c.platform) s += ` for ${c.platform}`;
    if (c.camera) s += `, played from a ${c.camera.toLowerCase()} perspective`;
    if (c.gameMode) s += ` as ${c.gameMode.toLowerCase()}`;
    s += ".";
    bits.push(s);
    if (c.mechanic) bits.push(`Core mechanic: "${c.mechanic}".`);
    if (c.emotion) bits.push(`The player should feel ${c.emotion.toLowerCase()}.`);
    if (c.audience) bits.push(`Aimed at ${c.audience.toLowerCase()}.`);
  }

  if (c.twist) {
    const hint = twistHint(c.twist);
    bits.push(`Theme / twist — ${c.twist}${hint ? `: ${hint}` : "."}`);
  }

  // Any extra (custom) axes the templated sentence didn't mention.
  const mentioned = new Set([...conceptKeys, "twist", "title"]);
  const extras = Object.entries(c)
    .filter(([k, v]) => !mentioned.has(k) && v)
    .map(([k, v]) => `${slotLabel(k)}: ${v}`);
  if (extras.length) bits.push(extras.join(" · ") + ".");

  return bits.join(" ");
}

function slotLabel(key: string): string {
  return JAM_SLOT_LABELS[key] ?? key;
}

export function conceptSummary(c: JamConcept): string {
  const vals = Object.entries(c)
    .filter(([k, v]) => k !== "title" && v)
    .map(([, v]) => v);
  return vals.join(" · ") || c.title;
}

/** Stable signature (ignoring the title) used to prevent duplicate saves. */
export function conceptSignature(c: JamConcept): string {
  return Object.keys(c)
    .filter((k) => k !== "title")
    .sort()
    .map((k) => `${k}:${c[k]}`)
    .join("|");
}

/** Encode a concept into a compact, shareable seed code (captures edits/clears). */
export function encodeConcept(c: JamConcept): string {
  const obj: Record<string, string> = {};
  for (const [k, v] of Object.entries(c)) if (typeof v === "string") obj[k] = v;
  return btoa(encodeURIComponent(JSON.stringify(obj)));
}

/** Decode a seed code back into a concept, or null if invalid. */
export function decodeConcept(code: string): JamConcept | null {
  try {
    const obj = JSON.parse(decodeURIComponent(atob(code.trim())));
    if (!obj || typeof obj.title !== "string") return null;
    const c: JamConcept = { title: obj.title };
    for (const [k, v] of Object.entries(obj)) if (k !== "title" && typeof v === "string") c[k] = v as string;
    return c;
  } catch {
    return null;
  }
}

/** Plain-text export of saved concepts. */
export function conceptsToText(concepts: JamConcept[]): string {
  return concepts.map((c) => `# ${c.title}\n${buildPitch(c)}\nSeed: ${encodeConcept(c)}`).join("\n\n");
}
